import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Table,
  Paper,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableSortLabel,
  CircularProgress,
} from '@mui/material';

import { QueryPreviewType, QueryPreviewMetadata } from 'types/types';

const sortRows = (
  array: { [key: string]: string }[],
  orderBy: string,
  order?: Order
) => {
  return array.sort((a, b) => {
    if (order === 'asc') {
      return a[orderBy] < b[orderBy] ? -1 : 1;
    } else {
      return a[orderBy] > b[orderBy] ? -1 : 1;
    }
  });
};

type Order = 'asc' | 'desc';

export interface Ref {
  handleShowLoader: (show: boolean) => void;
  handleQueryPreview: (queryPreview: QueryPreviewType) => void;
}

interface TableColumn {
  id: string;
  name: string;
}

const QueryPreview: React.ForwardRefRenderFunction<Ref> = (_, ref) => {
  const [orderBy, setOrderBy] = useState<string>('');
  const [columns, setColumns] = useState<TableColumn[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [order, setOrder] = useState<Order | undefined>('asc');
  const [data, setData] = useState<{ [key: string]: string }[]>([]);

  const handleShowLoader = (show: boolean) => setIsLoading(show);

  const handleSortRequest = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleOrderingColumns = (metadata: QueryPreviewMetadata[]) => {
    const tableColumn = metadata
      .filter(({ isHidden }) => !isHidden)
      .sort((a, b) => (a.columnIndex ?? 0) - (b.columnIndex ?? 0))
      .map(({ columnId = '', columnName = '' }) => ({
        id: columnId,
        name: columnName,
      }));

    return tableColumn;
  };

  const handleOrderData = (
    tableColumn: TableColumn[],
    data: {
      [key: string]: string;
    }[]
  ) => {
    const columnOrder = tableColumn.map(({ id }) => id);

    const reorderedData = data.map((item) =>
      Object.fromEntries(columnOrder.map((key) => [key, item[key] ?? '']))
    );

    return reorderedData;
  };

  const handleQueryPreview = (queryPreview: QueryPreviewType) => {
    const tableColumn = handleOrderingColumns(queryPreview.metadata);
    const reorderedData = handleOrderData(tableColumn, queryPreview.data);
    setColumns(tableColumn);
    setData(reorderedData);
  };

  useEffect(() => {
    const sortedRows = sortRows([...data], orderBy, order);
    setData(sortedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, orderBy]);

  useImperativeHandle(ref, () => ({ handleQueryPreview, handleShowLoader }));

  return (
    <div className="table-container">
      <TableContainer
        component={Paper}
        sx={{ maxHeight: '50vh', minHeight: '50vh' }}
      >
        {isLoading && (
          <div className="loading-overlay">
            <CircularProgress
              color="warning"
              className="circular-progess-icon"
            />
          </div>
        )}
        {!isLoading && columns.length > 0 && data.length > 0 && (
          <Table stickyHeader className="table-content" size="small">
            <TableHead>
              <TableRow>
                {columns.map((el) => (
                  <TableCell key={el.id} align="left" className="table-header">
                    <TableSortLabel
                      active={orderBy === el.id}
                      direction={orderBy === el.id ? order : 'asc'}
                      onClick={() => handleSortRequest(el.id)}
                    >
                      {el.name}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => {
                return (
                  <TableRow
                    key={index}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    {Object.keys(row).map((el, idx) => {
                      return (
                        <TableCell
                          key={`${row[el]}-${index}-${idx}`}
                          align="left"
                          className="table-cell"
                        >
                          {row[el]}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </div>
  );
};

export default forwardRef<Ref>(QueryPreview);
